// @ts-check

import { html } from "lit-html";
import {
  renderDetailRow,
  renderErrorMessage,
  renderInputField,
  renderPlaceholder,
  renderSectionCard,
  renderSelectField,
} from "../ui.js";
import {
  formatEuroEs,
  formatSpanishNumber,
  hasText,
  parseNumber,
  parseNumberOrDefault,
} from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createMortgageRentalCalculatorState>} MortgageRentalCalculatorState */
/** @typedef {{ kind: "empty" } | { kind: "error", error: string } | { kind: "ready", housePrice: number, reformCost: number, purchaseTax: number, notaryAndRegistry: number, agencyFee: number, hasMortgage: boolean, downpayment: number, mortgageAmount: number, interestRate: number, mortgageTerm: number, monthlyMortgagePayment: number, annualMortgagePayment: number, totalMortgageInterest: number, totalInvestment: number, cashInvested: number, grossAnnualRent: number, effectiveAnnualRent: number, totalYearlyCosts: number, netRentBeforeTax: number, incomeTax: number, netAnnualIncomeBeforeMortgage: number, netAnnualIncome: number, netMonthlyIncome: number, grossYield: number, netYieldOnTotal: number, cashOnCashReturn: number, paybackText: string }} MortgageRentalResult */

export const mortgageRentalCalculatorTool = {
  key: "mortgage",
  path: "/mortgage-rental-calculator",
  title: "Mortgage Rental Calculator",
  blurb: "Estimate rental yield, mortgage cash flow, and payback time.",
  badge: "Property",
  accentClass: "bg-gradient-to-r from-fuchsia-300 via-violet-300 to-purple-300",
  createState: createMortgageRentalCalculatorState,
  render: renderMortgageRentalCalculatorPage,
};

function createMortgageRentalCalculatorState() {
  return {
    housePrice: "",
    reformCost: "",
    purchaseTaxMode: "percent",
    purchaseTax: "",
    notaryAndRegistry: "",
    agencyFee: "",
    downpaymentMode: "percent",
    downpayment: "",
    interestRate: "",
    mortgageTerm: "",
    monthlyRent: "",
    occupancyRate: "92",
    ibi: "",
    communityFees: "",
    homeInsurance: "",
    maintenanceReserve: "5",
    managementFee: "",
    otherYearlyCosts: "",
    incomeTaxRate: "",
    rentalDeduction: "50",
  };
}

/**
 * @param {MortgageRentalCalculatorState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderMortgageRentalCalculatorPage(toolState, env) {
  const result = computeMortgageRentalResult(toolState);
  const purchaseTaxLabel = toolState.purchaseTaxMode === "percent"
    ? "ITP/IVA (%)"
    : "ITP/IVA (cantidad fija)";
  const downpaymentLabel = toolState.downpaymentMode === "percent"
    ? "Entrada (%)"
    : "Entrada (cantidad fija)";

  return html`
    <section class="grid gap-4">
      <div class="grid gap-4 xl:grid-cols-2 xl:items-start">
        ${renderSectionCard(
          "Datos de la inversión",
          "Coste de compra, impuestos y gastos iniciales.",
          html`
            <div class="grid gap-4 md:grid-cols-2">
              ${renderInputField({
                label: "Precio de la vivienda",
                value: toolState.housePrice,
                placeholder: "150000",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "housePrice", event);
                },
              })}
              ${renderInputField({
                label: "Coste de reforma",
                value: toolState.reformCost,
                placeholder: "0",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "reformCost", event);
                },
              })}
              ${renderSelectField({
                label: "Impuestos de compra",
                value: toolState.purchaseTaxMode,
                onInput: function (event) {
                  env.handleFieldInput(toolState, "purchaseTaxMode", event);
                },
                options: [
                  { value: "percent", label: "Porcentaje del precio" },
                  { value: "fixed", label: "Cantidad fija" },
                ],
              })}
              ${renderInputField({
                label: purchaseTaxLabel,
                value: toolState.purchaseTax,
                placeholder: "10",
                help: "ITP segunda mano: 6-10%. IVA obra nueva: 10%.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "purchaseTax", event);
                },
              })}
              ${renderInputField({
                label: "Notaría y registro",
                value: toolState.notaryAndRegistry,
                placeholder: "2000",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "notaryAndRegistry", event);
                },
              })}
              ${renderInputField({
                label: "Comisión de agencia",
                value: toolState.agencyFee,
                placeholder: "0",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "agencyFee", event);
                },
              })}
            </div>
          `,
        )}

        ${renderSectionCard(
          "Hipoteca",
          "Entrada, interés y plazo del préstamo.",
          html`
            <div class="grid gap-4 md:grid-cols-2">
              ${renderSelectField({
                label: "Entrada",
                value: toolState.downpaymentMode,
                onInput: function (event) {
                  env.handleFieldInput(toolState, "downpaymentMode", event);
                },
                options: [
                  { value: "percent", label: "Porcentaje del precio" },
                  { value: "fixed", label: "Cantidad fija" },
                ],
              })}
              ${renderInputField({
                label: downpaymentLabel,
                value: toolState.downpayment,
                placeholder: "20",
                help: "Los bancos suelen pedir 20-30% para inversión.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "downpayment", event);
                },
              })}
              ${renderInputField({
                label: "Tipo de interés anual (%)",
                value: toolState.interestRate,
                placeholder: "3.5",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "interestRate", event);
                },
              })}
              ${renderInputField({
                label: "Plazo hipoteca (años)",
                value: toolState.mortgageTerm,
                placeholder: "25",
                step: "1",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "mortgageTerm", event);
                },
              })}
            </div>
          `,
        )}
      </div>

      <div class="grid gap-4 xl:grid-cols-3 xl:items-start">
        ${renderSectionCard(
          "Ingresos por alquiler",
          "Alquiler mensual y ocupación media.",
          html`
            <div class="grid gap-4">
              ${renderInputField({
                label: "Alquiler mensual",
                value: toolState.monthlyRent,
                placeholder: "800",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "monthlyRent", event);
                },
              })}
              ${renderInputField({
                label: "Tasa de ocupación (%)",
                value: toolState.occupancyRate,
                placeholder: "92",
                help: "92% equivale a unos 11 meses ocupado al año.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "occupancyRate", event);
                },
              })}
            </div>
          `,
        )}

        ${renderSectionCard(
          "Gastos anuales",
          "IBI, comunidad, seguros y otros costes recurrentes.",
          html`
            <div class="grid gap-4">
              ${renderInputField({
                label: "IBI",
                value: toolState.ibi,
                placeholder: "500",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "ibi", event);
                },
              })}
              ${renderInputField({
                label: "Comunidad",
                value: toolState.communityFees,
                placeholder: "600",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "communityFees", event);
                },
              })}
              ${renderInputField({
                label: "Seguro hogar",
                value: toolState.homeInsurance,
                placeholder: "200",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "homeInsurance", event);
                },
              })}
              ${renderInputField({
                label: "Reserva mantenimiento (%)",
                value: toolState.maintenanceReserve,
                placeholder: "5",
                help: "Recomendado: 5-10% del alquiler anual.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "maintenanceReserve", event);
                },
              })}
              ${renderInputField({
                label: "Gestión de alquiler (%)",
                value: toolState.managementFee,
                placeholder: "0",
                help: "Si usas agencia: 8-12% es común.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "managementFee", event);
                },
              })}
              ${renderInputField({
                label: "Otros gastos anuales",
                value: toolState.otherYearlyCosts,
                placeholder: "0",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "otherYearlyCosts", event);
                },
              })}
            </div>
          `,
        )}

        ${renderSectionCard(
          "Impuestos sobre ingresos",
          "IRPF y reducción por alquiler de vivienda.",
          html`
            <div class="grid gap-4">
              ${renderInputField({
                label: "IRPF sobre rendimientos (%)",
                value: toolState.incomeTaxRate,
                placeholder: "19",
                help: "Tipo marginal habitual: 19-47%.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "incomeTaxRate", event);
                },
              })}
              ${renderInputField({
                label: "Reducción por alquiler (%)",
                value: toolState.rentalDeduction,
                placeholder: "50",
                help: "50% en alquiler de vivienda habitual desde 2024.",
                onInput: function (event) {
                  env.handleFieldInput(toolState, "rentalDeduction", event);
                },
              })}
            </div>
          `,
        )}
      </div>

      ${renderMortgageResults(result)}
    </section>
  `;
}

/**
 * @param {MortgageRentalResult} result
 * @returns {LitView}
 */
function renderMortgageResults(result) {
  if (result.kind === "empty") {
    return renderSectionCard(
      "Resultados",
      "La rentabilidad aparece cuando haya precio de vivienda y alquiler mensual válidos.",
      renderPlaceholder("Introduce al menos el precio de la vivienda y el alquiler mensual."),
    );
  }

  if (result.kind === "error") {
    return renderSectionCard(
      "Resultados",
      "Corrige la entrada inválida para recalcular la rentabilidad.",
      renderErrorMessage(result.error),
    );
  }

  const mortgageCard = result.hasMortgage
    ? html`
        <section class="rounded-lg bg-stone-100 p-5">
          <h3 class="text-base font-semibold text-stone-900">Hipoteca</h3>
          <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            ${renderDetailRow("Entrada", formatEuroEs(result.downpayment))}
            ${renderDetailRow("Importe hipoteca", formatEuroEs(result.mortgageAmount))}
            ${renderDetailRow("Tipo de interés", `${formatSpanishNumber(result.interestRate, 2)}%`)}
            ${renderDetailRow("Plazo", `${formatSpanishNumber(result.mortgageTerm, 0)} años`)}
            ${renderDetailRow("Cuota mensual", formatEuroEs(result.monthlyMortgagePayment), "", true)}
            ${renderDetailRow("Pago anual hipoteca", formatEuroEs(result.annualMortgagePayment))}
            ${renderDetailRow("Intereses totales", formatEuroEs(result.totalMortgageInterest))}
          </div>
        </section>
      `
    : "";

  return html`
    <section class="grid gap-4 xl:grid-cols-2 xl:items-start">
      <section class="rounded-lg bg-stone-100 p-5">
        <h3 class="text-base font-semibold text-stone-900">Inversión total</h3>
        <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          ${renderDetailRow("Precio vivienda", formatEuroEs(result.housePrice))}
          ${renderDetailRow("Reforma", formatEuroEs(result.reformCost))}
          ${renderDetailRow("Impuestos compra", formatEuroEs(result.purchaseTax))}
          ${renderDetailRow("Notaría y registro", formatEuroEs(result.notaryAndRegistry))}
          ${renderDetailRow("Comisión agencia", formatEuroEs(result.agencyFee))}
          ${renderDetailRow("Total inversión", formatEuroEs(result.totalInvestment), "", true)}
          ${result.hasMortgage ? renderDetailRow("Capital propio invertido", formatEuroEs(result.cashInvested)) : ""}
        </div>
      </section>

      ${mortgageCard}

      <section class="rounded-lg bg-stone-100 p-5">
        <h3 class="text-base font-semibold text-stone-900">Ingresos y gastos</h3>
        <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          ${renderDetailRow("Alquiler bruto anual", formatEuroEs(result.grossAnnualRent))}
          ${renderDetailRow("Alquiler efectivo anual", formatEuroEs(result.effectiveAnnualRent))}
          ${renderDetailRow("Total gastos anuales", formatEuroEs(result.totalYearlyCosts))}
          ${renderDetailRow("Rendimiento antes de IRPF", formatEuroEs(result.netRentBeforeTax))}
          ${renderDetailRow("IRPF", formatEuroEs(result.incomeTax))}
          ${renderDetailRow("Flujo anual antes de hipoteca", formatEuroEs(result.netAnnualIncomeBeforeMortgage), "", true)}
        </div>
      </section>

      <section class="rounded-lg bg-stone-100 p-5">
        <h3 class="text-base font-semibold text-stone-900">Rentabilidad</h3>
        <div class="mt-4 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          ${renderDetailRow("Flujo de caja anual", formatEuroEs(result.netAnnualIncome), result.netAnnualIncome >= 0 ? "text-emerald-700" : "text-red-600")}
          ${renderDetailRow("Flujo de caja mensual", formatEuroEs(result.netMonthlyIncome), result.netMonthlyIncome >= 0 ? "text-emerald-700" : "text-red-600")}
          ${renderDetailRow("Rentabilidad bruta", `${formatSpanishNumber(result.grossYield, 2)}%`, "", true)}
          ${renderDetailRow("Rentabilidad neta", `${formatSpanishNumber(result.netYieldOnTotal, 2)}%`)}
          ${result.hasMortgage ? renderDetailRow("Cash-on-cash return", `${formatSpanishNumber(result.cashOnCashReturn, 2)}%`) : ""}
          ${renderDetailRow("Tiempo para recuperar capital", result.paybackText)}
        </div>
      </section>
    </section>
  `;
}

/**
 * @param {MortgageRentalCalculatorState} toolState
 * @returns {MortgageRentalResult}
 */
function computeMortgageRentalResult(toolState) {
  if (!hasText(toolState.housePrice) || !hasText(toolState.monthlyRent)) {
    return { kind: "empty" };
  }

  const housePrice = parseNumber(toolState.housePrice);
  const reformCost = parseNumberOrDefault(toolState.reformCost, 0);
  const purchaseTaxInput = parseNumberOrDefault(toolState.purchaseTax, 0);
  const notaryAndRegistry = parseNumberOrDefault(toolState.notaryAndRegistry, 0);
  const agencyFee = parseNumberOrDefault(toolState.agencyFee, 0);
  const downpaymentInput = parseNumberOrDefault(toolState.downpayment, 0);
  const interestRate = parseNumberOrDefault(toolState.interestRate, 0);
  const mortgageTerm = parseNumberOrDefault(toolState.mortgageTerm, 0);
  const monthlyRent = parseNumber(toolState.monthlyRent);
  const occupancyRate = parseNumberOrDefault(toolState.occupancyRate, 100);
  const ibi = parseNumberOrDefault(toolState.ibi, 0);
  const communityFees = parseNumberOrDefault(toolState.communityFees, 0);
  const homeInsurance = parseNumberOrDefault(toolState.homeInsurance, 0);
  const maintenanceReserve = parseNumberOrDefault(toolState.maintenanceReserve, 0);
  const managementFee = parseNumberOrDefault(toolState.managementFee, 0);
  const otherYearlyCosts = parseNumberOrDefault(toolState.otherYearlyCosts, 0);
  const incomeTaxRate = parseNumberOrDefault(toolState.incomeTaxRate, 0);
  const rentalDeduction = parseNumberOrDefault(toolState.rentalDeduction, 0);

  if (!Number.isFinite(housePrice) || housePrice <= 0) {
    return { kind: "error", error: "Introduce un precio de vivienda válido." };
  }

  if (!Number.isFinite(monthlyRent) || monthlyRent <= 0) {
    return { kind: "error", error: "Introduce un alquiler mensual válido." };
  }

  const purchaseTax = toolState.purchaseTaxMode === "percent"
    ? housePrice * (purchaseTaxInput / 100)
    : purchaseTaxInput;
  const downpayment = toolState.downpaymentMode === "percent"
    ? housePrice * (downpaymentInput / 100)
    : downpaymentInput;
  const mortgageAmount = Math.max(0, housePrice - downpayment);
  const hasMortgage = mortgageAmount > 0 && interestRate > 0 && mortgageTerm > 0;

  let monthlyMortgagePayment = 0;
  let annualMortgagePayment = 0;
  let totalMortgageInterest = 0;
  if (hasMortgage) {
    const monthlyRate = interestRate / 100 / 12;
    const numPayments = mortgageTerm * 12;
    const rateFactor = Math.pow(1 + monthlyRate, numPayments);
    monthlyMortgagePayment =
      (mortgageAmount * (monthlyRate * rateFactor)) / (rateFactor - 1);
    annualMortgagePayment = monthlyMortgagePayment * 12;
    totalMortgageInterest = monthlyMortgagePayment * numPayments - mortgageAmount;
  }

  const totalInvestment =
    housePrice + reformCost + purchaseTax + notaryAndRegistry + agencyFee;
  const cashInvested =
    downpayment + reformCost + purchaseTax + notaryAndRegistry + agencyFee;
  const grossAnnualRent = monthlyRent * 12;
  const effectiveAnnualRent = grossAnnualRent * (occupancyRate / 100);
  const maintenanceCost = grossAnnualRent * (maintenanceReserve / 100);
  const managementCost = effectiveAnnualRent * (managementFee / 100);
  const totalYearlyCosts =
    ibi + communityFees + homeInsurance + maintenanceCost + managementCost + otherYearlyCosts;
  const netRentBeforeTax = effectiveAnnualRent - totalYearlyCosts;
  const taxableIncome = netRentBeforeTax * (1 - rentalDeduction / 100);
  const incomeTax = taxableIncome > 0 ? taxableIncome * (incomeTaxRate / 100) : 0;
  const netAnnualIncomeBeforeMortgage = netRentBeforeTax - incomeTax;
  const netAnnualIncome = netAnnualIncomeBeforeMortgage - annualMortgagePayment;
  const netMonthlyIncome = netAnnualIncome / 12;
  const grossYield = (grossAnnualRent / totalInvestment) * 100;
  const netYieldOnTotal = (netAnnualIncomeBeforeMortgage / totalInvestment) * 100;
  const cashOnCashReturn = cashInvested > 0 ? (netAnnualIncome / cashInvested) * 100 : 0;
  const paybackYears = netAnnualIncome > 0
    ? cashInvested / netAnnualIncome
    : Number.POSITIVE_INFINITY;
  const paybackMonths = paybackYears * 12;
  const paybackText = paybackYears === Number.POSITIVE_INFINITY
    ? "No se recupera (flujo de caja negativo)"
    : `${formatSpanishNumber(paybackYears, 1)} años (${Math.round(paybackMonths)} meses)`;

  return {
    kind: "ready",
    housePrice,
    reformCost,
    purchaseTax,
    notaryAndRegistry,
    agencyFee,
    hasMortgage,
    downpayment,
    mortgageAmount,
    interestRate,
    mortgageTerm,
    monthlyMortgagePayment,
    annualMortgagePayment,
    totalMortgageInterest,
    totalInvestment,
    cashInvested,
    grossAnnualRent,
    effectiveAnnualRent,
    totalYearlyCosts,
    netRentBeforeTax,
    incomeTax,
    netAnnualIncomeBeforeMortgage,
    netAnnualIncome,
    netMonthlyIncome,
    grossYield,
    netYieldOnTotal,
    cashOnCashReturn,
    paybackText,
  };
}
